import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {externalConfigs, saveExternalConfigs} from '../../../store/reducers/externalConfig';
import {useAgyleConfigStyles} from '../agyle_configuration';
import FormLabel from '@mui/material/FormLabel';
import React, {useEffect, useState} from 'react';
import {Divider, Paper} from '@mui/material';
import _ from 'lodash';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {AgyleTable, createAgyleTableColumn} from '../../../components/table/AgyleTable';
import IconButton from '@mui/material/IconButton';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import uuidv4 from 'uuid/v4';
import {setSnackbar} from '../../../store/reducers/globals';

export const DEFAULT_CFG_FLASHING = [
    {id: 'def-1', colour: 'red', type: 'obsDue', time: null},
    {id: 'def-2', colour: 'red', type: 'noObs', time: null},
    {id: 'def-3', colour: 'amber', type: 'obsDueTime', time: 15},
    {id: 'def-4', colour: 'blue', type: 'sepsis', time: null},
];

const CONFIG_COLOR_OPTIONS = [
    {id: 'obsDueTime', name: 'Observations due in', value: 'obsDueTime', defaultTime: 15},
    {id: 'obsDue', name: 'Observations due', value: 'obsDue', defaultTime: 0},
    {id: 'noObs', name: 'No Observations', value: 'noObs', defaultTime: 0},
    {id: 'sepsis', name: 'Sepsis pathway active', value: 'sepsis', defaultTime: 0},
    {id: 'breachTime', name: 'Flash in since arrival', value: 'breachTime', defaultTime: 180},
    {id: 'breach4h', name: 'Breach 4hr', value: 'breach4h', defaultTime: 0},
    {id: 'cancelInTime', name: 'Cancel flashing in', value: 'cancelIn', defaultTime: 720},
    {id: 'noCCTime', name: 'No current clinician set after', value: 'noCCTime', defaultTime: 120},
    {id: 'noDiagTime', name: 'No diagnosis from set clinician', value: 'noDiagTime', defaultTime: 60},
    {id: 'referralTime', name: 'Flash after specialty referral in', value: 'referralTime', defaultTime: 720},
    {id: 'cancelReferralInTime', name: 'Stop flashing after specialty referral in', value: 'cancelReferralInTime', defaultTime: 780},
];

const COLORS_OPTIONS = [
    {id: 'amber', name: 'Amber', value: 'amber'},
    {id: 'red', name: 'Red', value: 'red'},
    {id: 'blue', name: 'Blue', value: 'blue'},
];


const COLUMNS = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('colour', 'Colour', 2, 'colour', 'startCase', 'notSort', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('behaviour', 'Behaviour', 3, 'type', 'fromDict', 'notSort', 'medium', 'left', true, false, null, 300, [{
        dict: CONFIG_COLOR_OPTIONS,
        searchField: 'id',
        displayField: 'name',
    }]),
    createAgyleTableColumn('time', 'Time (in minutes)', 4, 'time', 'number', 'notSort', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('action', 'Action', 5, null, 'action', 'notSort', 'medium', 'right', true, false, null, null, null),
];

const PtCardCfg = ({context}) => {
    const classes = useAgyleConfigStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const cfgs = useSelector(externalConfigs);
    const [savedConfig, setSavedConfig] = useState(null);
    const [value, setValue] = useState(savedConfig?.value || DEFAULT_CFG_FLASHING);
    const [form, setForm] = useState({
        colour: '',
        type: '',
        time: '',
    });


    useEffect(() => {
        const find = _.find(cfgs, cfg => cfg.configType === context?.id);
        if (find) setSavedConfig(find);
    }, [cfgs]);

    useEffect(() => {
        setValue(savedConfig?.value || DEFAULT_CFG_FLASHING);
    }, [savedConfig]);

    const handleResetToDefault = () => {
        setValue(DEFAULT_CFG_FLASHING);
    };

    const handleSave = () => {
        const objToSave = {
            __id: savedConfig?.__id || null,
            description: context.description,
            configType: context.id,
            active: true,
            value,
        };

        dispatch(saveExternalConfigs(objToSave));
    };

    const removeFormList = (obj) => {
        let clone = _.cloneDeep(value);
        const index = _.findIndex(clone, {id: obj.id});
        if (index > -1) {
            clone.splice(index, 1);
        }
        setValue(clone);
    };

    const handleChangeNumber = (e) => {
        const {name, value} = e.target;
        if (!_.isNaN(Number(value))) {
            handleChangeForm(name, value);
        }
    };

    const handleChangeType = (val) => {
        const obj = {
            ...form,
            type: val,
            time: '',
        };

        if (_.endsWith(val, 'Time')) {
            const find = _.find(CONFIG_COLOR_OPTIONS, {id: val});
            obj.time = find?.defaultTime || '';
            if (_.startsWith(val,'cancel')) {
                obj.colour = '';
            }
        }

        setForm(obj);
    };

    const handleChangeForm = (name, val) => {
        setForm(prevState => ({
            ...prevState,
            [name]: val,
        }));
    };

    const valid = () => {
        let isValid = (_.startsWith(form.type,'cancel') ? true : !_.isEmpty(form.colour)) && !_.isEmpty(form.type)
            && (_.endsWith(form.type, 'Time') ? form.time !== '' : true);

        if (!isValid) {
            dispatch(setSnackbar({type: 'warning', message: t('Not all fields have been filled in.')}));
        }

        return isValid;
    };

    const handleApply = () => {
        if (valid()) {
            const obj = {
                id: uuidv4() + '',
                ...form,
            };
            const val = _.cloneDeep(value);
            val.push(obj);
            setValue(val);
            setForm({
                colour: '',
                type: '',
                time: '',
            });
            dispatch(setSnackbar({type: 'success', message: t('Trigger added.')}));
        }
    };

    const swapArrayLocs = (arr, index1, index2) => {
        [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
    };

    const handleChangePos = (obj, isUp) => {
        let clone = _.cloneDeep(value);
        const index = _.findIndex(clone, {id: obj.id});

        swapArrayLocs(clone, index, index + (isUp ? -1 : 1));
        setValue(clone);
    };

    const renderCustomButtons = (obj) => {
        const index = _.findIndex(value, {id: obj.id});

        return (
            <div>
                <IconButton
                    size="small"
                    disabled={index < 1}
                    onClick={handleChangePos.bind(this, obj, true)}
                >
                    <ArrowDropUpIcon/>
                </IconButton>

                <IconButton
                    size="small"
                    disabled={index >= (value?.length - 1)}
                    onClick={handleChangePos.bind(this, obj, false)}
                >
                    <ArrowDropDownIcon/>
                </IconButton>
            </div>
        );
    };

    const renderPriorityTable = () => {

        return (
            <div>
                <AgyleTable
                    disableFilter
                    columns={COLUMNS}
                    data={value}
                    deleteItem={removeFormList}
                    customActionItem={renderCustomButtons}
                    tableContainerClass={classes.table}
                    edRefresh
                />
                <div className={classes.action}>
                    <AgyleButton
                        onClick={handleResetToDefault}
                        variant="contained"
                    >
                        {t('Reset to default')}
                    </AgyleButton>

                    <AgyleButton
                        onClick={handleSave}
                        variant="contained"
                    >
                        {t('Save changes')}
                    </AgyleButton>
                </div>
            </div>
        );
    };

    return (
        <>
            <Paper className={classes.paper}>
                <FormLabel className={classes.label}>
                    {t(context?.description)}
                </FormLabel>

                {renderPriorityTable()}

                <Divider style={{margin: 10}}/>


                <div className={classes.currentOpt}>
                    <FormLabel className={classes.label}>
                        {t('Add to priority table')}
                    </FormLabel>

                    <AgyleSelectField
                        label={t('Trigger')}
                        onChange={handleChangeType}
                        value={form.type}
                        options={CONFIG_COLOR_OPTIONS}
                        withBackground
                        clearButtonVisible={false}
                    />

                    <AgyleTextField
                        label={t('Time (in minutes)')}
                        name="time"
                        value={form.time}
                        onChange={handleChangeNumber}
                        type="number"
                        fullWidth
                        shrink={!!form.time}
                        disabled={!_.endsWith(form.type, 'Time')}
                    />

                    <AgyleSelectField
                        label={t('Colour')}
                        onChange={(val) => handleChangeForm('colour', val)}
                        value={form.colour}
                        options={COLORS_OPTIONS}
                        withBackground
                        disabled={_.startsWith(form.type, 'cancel')}
                    />

                    <AgyleButton
                        onClick={handleApply}
                        variant="contained"
                        style={{marginLeft: 'auto'}}
                    >
                        {t('Add trigger')}
                    </AgyleButton>
                </div>
            </Paper>
        </>
    );
};

export default PtCardCfg;